import {
  Button,
  CircularProgress,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { onlyNumbers } from 'shared/helpers/string';
import { usePermissions } from 'shared/hooks/usePermissions';
import { documentMasks, phoneMasks } from 'shared/utils/maskOptions';
import { useManagedAccountModalContext } from './ModalContext';

interface ManagedAccountModalProps {
  isOpen: boolean;
  onClose(): void;
}

const ManagedAccountModal: React.FC<ManagedAccountModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    loading,
    account,
    errors,
    handleSubmit,
    handleDisableAccount,
    setAccount,
  } = useManagedAccountModalContext();

  const { d } = usePermissions();

  const [formData, setFormData] = useState({
    document: '',
    companyName: '',
    phone: '',
    email: '',
    nickname: '',
  });

  useEffect(() => {
    if (isOpen && account) {
      let formattedPhone = account.managed_account_phone_number || '';
      if (formattedPhone && formattedPhone.length >= 10) {
        const digits = formattedPhone.replace(/\D/g, '');
        if (digits.length === 10) {
          formattedPhone = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11) {
          formattedPhone = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
        }
      }

      setFormData({
        document: account.managed_account_document || '',
        companyName: account.managed_account_trade_name || '',
        phone: formattedPhone,
        email: account.managed_account_email || '',
        nickname: account.managed_account_alias || '',
      });
    }
  }, [isOpen, account]);

  const handleInputChange = (field: string, value: string) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };
    setFormData(newFormData);

    if (account) {
      const updatedAccount = { ...account };
      switch (field) {
        case 'document':
          const docDigits = onlyNumbers(value);
          updatedAccount.managed_account_document = docDigits;
          break;
        case 'companyName':
          updatedAccount.managed_account_trade_name = value;
          break;
        case 'phone':
          const phoneDigits = onlyNumbers(value);
          updatedAccount.managed_account_phone_number = phoneDigits;
          break;
        case 'email':
          updatedAccount.managed_account_email = value;
          break;
        case 'nickname':
          updatedAccount.managed_account_alias = value;
          break;
      }
      setAccount(updatedAccount);
    }
  };

  const handleDisableClick = () => {
    if (account) {
      handleDisableAccount(account);
    }
  };

  const isFormValid = () => {
    return (
      formData.document.trim() !== '' &&
      formData.companyName.trim() !== '' &&
      formData.phone.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.nickname.trim() !== ''
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent pos="relative">
        <DrawerCloseButton />
        <DrawerHeader>Contas Gerenciáveis</DrawerHeader>

        <Divider />

        <DrawerBody>
          <Flex justify="space-between" align="center" mb="1.5rem" mt="1rem">
            <Heading size="md">Dados da conta</Heading>
            <Button
              backgroundColor={'red.100'}
              variant="solid"
              color="yRed.500"
              onClick={handleDisableClick}
              isLoading={loading}
              isDisabled={
                account?.is_managed_account === false ||
                d('managed-account-disable')
              }
            >
              Desativar
            </Button>
          </Flex>
          <Flex columnGap="5%" rowGap="2rem" wrap="wrap">
            <FormControl
              isRequired
              isInvalid={!!errors.managed_account_document}
              flexBasis="37.5%"
            >
              <FormLabel fontSize="1rem">Documento</FormLabel>
              <Input
                as={IMaskInput}
                placeholder="00.000.000/0000-00"
                mask={documentMasks}
                value={formData.document}
                onAccept={(value: string) =>
                  handleInputChange('document', value)
                }
              />
              {errors.managed_account_document && (
                <FormErrorMessage>
                  {errors.managed_account_document}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.managed_account_trade_name}
              flexBasis="57.5%"
            >
              <FormLabel fontSize="1rem">Razão Social</FormLabel>
              <Input
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange('companyName', e.target.value)
                }
                placeholder="Nome da empresa"
              />
              {errors.managed_account_trade_name && (
                <FormErrorMessage>
                  {errors.managed_account_trade_name}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.managed_account_phone_number}
              flexBasis="37.5%"
            >
              <FormLabel fontSize="1rem">Telefone</FormLabel>
              <Input
                as={IMaskInput}
                placeholder="(00) 00000-0000"
                mask={phoneMasks}
                value={formData.phone}
                onAccept={(value: string) => handleInputChange('phone', value)}
              />
              {errors.managed_account_phone_number && (
                <FormErrorMessage>
                  {errors.managed_account_phone_number}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.managed_account_email}
              flexBasis="57.5%"
            >
              <FormLabel fontSize="1rem">Email</FormLabel>
              <Input
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="email@exemplo.com"
                type="email"
              />
              {errors.managed_account_email && (
                <FormErrorMessage>
                  {errors.managed_account_email}
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.managed_account_alias}>
              <FormLabel fontSize="1rem">Apelido</FormLabel>
              <Input
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                placeholder="Apelido da conta"
              />
              {errors.managed_account_alias && (
                <FormErrorMessage>
                  {errors.managed_account_alias}
                </FormErrorMessage>
              )}
            </FormControl>
          </Flex>

          <Flex my={8} gap={6} px={8}>
            <Button
              flex={1}
              variant="emphasisPrimary"
              iconSpacing="0"
              size="lg"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={!isFormValid() || d('managed-account-update')}
              title={d('managed-account-update') ? 'Sem permissão' : undefined}
            >
              Salvar
            </Button>
          </Flex>
        </DrawerBody>

        {loading && (
          <Flex
            pos="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            bg="gray.100"
            zIndex="1"
            align="center"
            justify="center"
          >
            <CircularProgress isIndeterminate color="yooga.400" />
          </Flex>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default ManagedAccountModal;
